import { createTransport } from "@/src/config/transportFactory.js";
import { transport as mainTransport, BASE_PATH } from "@/src/config/apiConfig.js";

const { transport } = createTransport("digitalEmployee");

export default {
  // ── Employees ──────────────────────────────────────────────

  async listEmployees(page = 1, pageSize = 20, { status, search, filters } = {}) {
    try {
      const params = new URLSearchParams();
      params.append("page", page);
      params.append("page_size", pageSize);
      if (status) params.append("status", status);
      if (search) params.append("search", search);
      if (filters?.length) params.append("filters", JSON.stringify(filters));
      const res = await transport.get(`/v1/digital-employees?${params}`);
      return res.data;
    } catch (error) {
      console.error("Error listing employees:", error);
      throw error;
    }
  },

  async getEmployee(id) {
    try {
      const res = await transport.get(`/v1/digital-employees/${id}`);
      return res.data.digital_employee;
    } catch (error) {
      console.error("Error getting employee:", error);
      throw error;
    }
  },

  async createEmployee(payload) {
    try {
      const res = await transport.post("/v1/digital-employees", payload);
      return res.data.digital_employee;
    } catch (error) {
      console.error("Error creating employee:", error);
      throw error;
    }
  },

  async updateEmployee(id, payload) {
    try {
      const res = await transport.put(`/v1/digital-employees/${id}`, payload);
      return res.data.digital_employee;
    } catch (error) {
      console.error("Error updating employee:", error);
      throw error;
    }
  },

  async updateEmployeeStatus(id, status) {
    try {
      const res = await transport.patch(`/v1/digital-employees/${id}/status`, { status });
      return res.data.digital_employee;
    } catch (error) {
      console.error("Error updating employee status:", error);
      throw error;
    }
  },

  async getEmployeeEmbed(id) {
    try {
      const res = await transport.get(`/v1/digital-employees/${id}/embed`);
      return res.data;
    } catch (error) {
      console.error("Error getting employee embed:", error);
      throw error;
    }
  },

  async deleteEmployee(id) {
    try {
      await transport.delete(`/v1/digital-employees/${id}`);
    } catch (error) {
      console.error("Error deleting employee:", error);
      throw error;
    }
  },

  async listAvatars() {
    try {
      const res = await transport.get("/v1/avatars");
      return {
        avatars: res.data?.avatars ?? [],
        defaultAvatarId: res.data?.default_avatar_id || null,
      };
    } catch (error) {
      console.error("Error listing avatars:", error);
      throw error;
    }
  },

  // ── Views (Rails API) ──────────────────────────────────────

  async fetchEmployeeViews() {
    try {
      const res = await mainTransport.get(`${BASE_PATH}/api/employees/views`);
      return res.data;
    } catch (error) {
      console.error("Error fetching digital employee views:", error);
      throw error;
    }
  },

  async fetchSessionViews() {
    try {
      const res = await mainTransport.get(`${BASE_PATH}/api/digital_employee_session/views`);
      return res.data;
    } catch (error) {
      console.error("Error fetching session views:", error);
      throw error;
    }
  },

  async applySessionFilter(viewId, payload) {
    try {
      const res = await mainTransport.put(`${BASE_PATH}/api/views/${viewId}`, { view: payload });
      return res.data;
    } catch (error) {
      console.error("Error applying session filter:", error);
      throw error;
    }
  },

  // ── Sessions / Meetings ────────────────────────────────────

  async listSessions(employeeId, page = 1, pageSize = 20, { status, search, time_range } = {}) {
    try {
      const params = new URLSearchParams();
      params.append("page", page);
      params.append("page_size", pageSize);
      if (status) params.append("status", status);
      if (search) params.append("search", search);
      if (time_range) params.append("time_range", JSON.stringify(time_range));
      const res = await transport.get(`/v1/digital-employees/${employeeId}/meetings?${params}`);
      return res.data;
    } catch (error) {
      console.error("Error listing sessions:", error);
      throw error;
    }
  },

  async getSession(sessionId) {
    try {
      const res = await transport.get(`/v1/meetings/${sessionId}`);
      return res.data.meeting;
    } catch (error) {
      console.error("Error getting session:", error);
      throw error;
    }
  },

  async getSessionMessages(sessionId, page, pageSize, search) {
    try {
      let url = `/v1/meetings/${sessionId}/messages`;
      const params = [];
      if (page) params.push(`page=${page}`);
      if (pageSize) params.push(`page_size=${pageSize}`);
      if (search) params.push(`search=${encodeURIComponent(search)}`);
      if (params.length) url += `?${params.join("&")}`;
      const res = await transport.get(url);
      return res.data.items;
    } catch (error) {
      console.error("Error getting session messages:", error);
      throw error;
    }
  },

  async deleteSession(meetingId) {
    try {
      await transport.delete(`/v1/meetings/${meetingId}`);
    } catch (error) {
      console.error("Error deleting meeting:", error);
      throw error;
    }
  },

  async getRecordingURL(meetingId) {
    try {
      const res = await transport.get(`/v1/meetings/${meetingId}/recording-url`);
      return res.data;
    } catch (error) {
      console.error("Error getting recording URL:", error);
      throw error;
    }
  },

  // ── Meeting Notes ──────────────────────────────────────────

  async listNotes(sessionId) {
    try {
      const res = await transport.get(`/v1/meetings/${sessionId}/notes`);
      return res.data.notes;
    } catch (error) {
      console.error("Error listing meeting notes:", error);
      throw error;
    }
  },

  async createNote(sessionId, content) {
    try {
      const res = await transport.post(`/v1/meetings/${sessionId}/notes`, { content });
      return res.data.note;
    } catch (error) {
      console.error("Error creating meeting note:", error);
      throw error;
    }
  },

  async updateNote(sessionId, noteId, content) {
    try {
      const res = await transport.put(`/v1/meetings/${sessionId}/notes/${noteId}`, { content });
      return res.data.note;
    } catch (error) {
      console.error("Error updating meeting note:", error);
      throw error;
    }
  },

  async deleteNote(sessionId, noteId) {
    try {
      await transport.delete(`/v1/meetings/${sessionId}/notes/${noteId}`);
    } catch (error) {
      console.error("Error deleting meeting note:", error);
      throw error;
    }
  },

  // ── Meeting Summary ────────────────────────────────────────

  async getSummary(sessionId) {
    try {
      const res = await transport.get(`/v1/meetings/${sessionId}/summary`);
      return res.data.summary;
    } catch (error) {
      console.error("Error getting meeting summary:", error);
      throw error;
    }
  },

  // ── Content Cards ──────────────────────────────────────────

  async listContent(employeeId) {
    try {
      const res = await transport.get(`/v1/digital-employees/${employeeId}/content-cards`);
      return res.data;
    } catch (error) {
      console.error("Error listing content:", error);
      throw error;
    }
  },

  async replaceContentCards(employeeId, cards) {
    try {
      const res = await transport.put(`/v1/digital-employees/${employeeId}/content-cards`, { cards });
      return res.data;
    } catch (error) {
      console.error("Error replacing content cards:", error);
      throw error;
    }
  },

  async uploadContentFile(file, filenameOrOpts) {
    try {
      const fd = new FormData();
      let filename;
      let signal;
      if (typeof filenameOrOpts === "string") {
        filename = filenameOrOpts;
      } else if (filenameOrOpts && typeof filenameOrOpts === "object") {
        filename = filenameOrOpts.filename;
        signal = filenameOrOpts.signal;
      }
      fd.append("file", file, filename || (file instanceof File ? file.name : "image.png"));
      const config = { headers: { "Content-Type": undefined } };
      if (signal) config.signal = signal;
      const res = await transport.post("/v1/upload", fd, config);
      return res.data;
    } catch (error) {
      console.error("Error uploading file:", error);
      throw error;
    }
  },
};
